import {
  RISK_EXTENSION_GAP_HIERARCHAL_STATUS,
  RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECT,
  RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECTS,
  RISK_EXTENSION_GAP_STATUS_UPDATE_CREATED,
  RISK_EXTENSION_GAP_STATUS_UPDATE_CREATED_BY,
  RISK_EXTENSION_GAP_STATUS_UPDATE_NOTE,
  RISK_EXTENSION_GAP_STATUS_UPDATE_STATUS,
  RISK_EXTENSION_GAP_STATUS_UPDATE_SUBSTATUSES,
  RISK_EXTENSION_GAP_STATUS_UPDATES,
  SYSTEM_ZUS_GAP_SOURCE,
  ZUS_SUPERSEDED_BY_SYSTEM,
} from '@ctw/shared/api/fhir/system-urls';
import type { ResourceTypeString } from '@ctw/shared/api/fhir/types';
import type { FetchableResource } from '@ctw/shared/context/_internal/resource/resource-fetch';
import {
  formatDateToLocalString,
  formatFHIRDate,
  getYearFromISODate,
} from '@ctw/shared/utils/dates';
import {
  isFetchableResourceType,
  isValidResourceId,
} from '@ctw/shared/utils/fhir/resource-mappings';
import type { NonEmptyArray } from '@ctw/shared/utils/types';
import {
  CODE_SYSTEMS,
  RISK_GAP_CONFIDENCE_RATINGS,
  RISK_GAP_VALUES,
  type RiskGapMeasureReport,
  type RiskGapStatus,
  type RiskGapConfidenceRating as SharedPackageRiskGapConfidenceRating,
  type RiskGapSource as SharedPackageRiskGapSource,
  type RiskGapType as SharedPackageRiskGapType,
  SYSTEM_URLS,
  UnvalidatedRiskGapMeasureReportModel,
  type ZusExtensionInterface,
} from '@zeus-health/types';
import { formatISO } from 'date-fns';
import type { Coding } from 'fhir/r4';
import { compact } from 'lodash-es';
import { FHIRModel } from './fhir-model';

/**
 * @deprecated
 * Legacy sources used to map to Builder names.
 * These sources no longer appear in real data.
 * @see https://app.metaplane.dev/monitor/019d4ec2-9ae3-7392-b3da-e6d05281e7fe
 */
const payerMapping = {
  'homeward-payer-data': 'Homeward',
} as const;

/** @deprecated use {@link SharedPackageRiskGapType RiskGapType} in @zeus-health/types instead */
export type RiskGapType = SharedPackageRiskGapType | 'unknown'; // unknown type does not exist in real data: https://app.metaplane.dev/monitor/019d4ec2-9ae3-7392-b3da-e6d05281e7fe
/** @deprecated use {@link SharedPackageRiskGapSource RiskGapSource} in @zeus-health/types instead */
export type RiskGapSource =
  // these non-SharedPackageRiskGapSource values no longer exist in real data: https://app.metaplane.dev/monitor/019df5ee-00af-7cfb-83dd-6efef41fd78b
  'zus-recapture-model' | 'zus-suspect-model' | 'homeward-payer-data' | SharedPackageRiskGapSource;
/** @deprecated use {@link SharedPackageRiskGapSource RiskGapSource} in @zeus-health/types instead */
export type NewGapSource = SharedPackageRiskGapSource | 'user-update'; // user-update does not exist in real data: https://app.metaplane.dev/monitor/019df5ee-00af-7cfb-83dd-6efef41fd78b
/** @deprecated use {@link SharedPackageRiskGapType RiskGapType} in @zeus-health/types instead */
export type NewGapType = SharedPackageRiskGapType;

export interface RiskGapICD10Code {
  code: string;
  display: string;
  lastEncounterDiagnosisRecordedDate?: string;
  sources: Array<string>;
  dataSourceType: Array<string>;
}

/** @deprecated custom mapping is no longer supported */
const isPayerSource = (
  source: RiskGapSource | NewGapSource | undefined,
): source is keyof typeof payerMapping => !!source && source in payerMapping;

/** @deprecated custom payer display is no longer supported */
const getPayerDisplay = (source?: RiskGapSource | NewGapSource): string =>
  payerMapping[source as keyof typeof payerMapping] ?? 'Payer';

// Most mappings only map to themselves. This is intentional since we handle most of our migrations on the backend.
export const riskGapStatusMappings: Record<string, RiskGapBackwardsCompatibleStatus> = {
  // 'open' is a deprecated status value replaced by 'open-gap' in @zeus-health/types 1.2.0.
  // ~259 legacy FHIR MeasureReport records still store 'open' as their status code.
  // Without this mapping, those records pass through as 'open' which is not a modern canonical
  // RiskGapStatus and can cause type mismatches and incorrect UI rendering.
  open: 'open-gap',
  'open-gap': 'open-gap',
  // invalid-gap maps to open-gap when normalized via normalizeToBackwardsCompatibleStatus
  'accepted-for-review': 'accepted-for-review',
  pending: 'pending',
  dismissed: 'dismissed',
  'expected-to-close': 'expected-to-close',
  'expected-to-close-1st-party': 'expected-to-close-1st-party',
  'expected-to-close-3rd-party': 'expected-to-close-3rd-party',
  // Any legacy closed statuses are not editable by the user.
  // we keep them unchanged in the frontend
  // we list them here for compliance with types
  'closed-gap': 'closed-gap',
  'closed-by-payer-file': 'closed-by-payer-file',
  'closed-by-claims-file': 'closed-by-claims-file',
  'closed-unspecified': 'closed-unspecified',
  closed: 'closed',
} as const;

/**
 * Normalizes deprecated status values (e.g. 'open' → 'open-gap') to their
 * canonical equivalents via riskGapStatusMappings. Falls back to the raw value
 * if it is a recognized status, or defaults to 'open-gap' for unknown values.
 */
const normalizeToBackwardsCompatibleStatus = (status: string): RiskGapBackwardsCompatibleStatus =>
  (riskGapStatusMappings[status] ??
    (isRiskGapStatus(status) ? status : 'open-gap')) as RiskGapBackwardsCompatibleStatus;

/** Statuses that users can select and set through the UI. */
export const riskGapUserStatusOptions = [
  {
    label: 'Open',
    value: RISK_GAP_VALUES.MEASURE_REPORT.EXTENSION.RISK_GAP_STATUS_UPDATE.OPEN_GAP, // 'open-gap'
  },
  {
    label: 'Pending',
    value: RISK_GAP_VALUES.MEASURE_REPORT.EXTENSION.RISK_GAP_STATUS_UPDATE.PENDING, // 'pending'
  },
  {
    label: 'Accepted for review',
    value: RISK_GAP_VALUES.MEASURE_REPORT.EXTENSION.RISK_GAP_STATUS_UPDATE.ACCEPTED_FOR_REVIEW, // 'accepted-for-review'
  },
  {
    label: 'Dismissed',
    value: RISK_GAP_VALUES.MEASURE_REPORT.EXTENSION.RISK_GAP_STATUS_UPDATE.DISMISSED, // 'dismissed'
  },
] as const satisfies Array<{ label: string; value: RiskGapStatus }>;

/**
 * Current statuses that are set by the system only (payer files, automation).
 * Users cannot select these in the status dialog but can transition away from them
 * (e.g. reopening a closed gap). Distinct from legacyRiskGapSystemStatusOptions which
 * contains deprecated values from older data.
 */
export const riskGapSystemStatusOptions = [
  {
    label: 'Closed by payer file',
    value: RISK_GAP_VALUES.MEASURE_REPORT.EXTENSION.RISK_GAP_STATUS_UPDATE.CLOSED_GAP, // 'closed-gap'
  },
] as const satisfies Array<{ label: string; value: RiskGapStatus }>;

export type RiskGapUserStatus = (typeof riskGapUserStatusOptions)[number]['value'];

export const isRiskGapUserStatus = (status: string): status is RiskGapUserStatus =>
  riskGapUserStatusOptions.some((option) => option.value === status);

/** Checks if a status is a current system-managed status (e.g. 'closed-gap'). */
export const isRiskGapSystemStatus = (status: string): status is RiskGapSystemStatus =>
  riskGapSystemStatusOptions.some((option) => option.value === status);

export const isRiskGapStatus = (status: string): status is RiskGapBackwardsCompatibleStatus =>
  riskGapStatusOptions.some((option) => option.value === status);

/**
 * Some values were technically RiskGapStatus values in @zeus-health/types but are
 * now deprecated. Some have yet to be fully cleaned up (see inline comments for estimated amounts).
 * See up to date counts in metaplane monitor https://app.metaplane.dev/monitor/019d4ebb-7d12-7d18-a8ac-ccb9446893fb
 */
export const legacyRiskGapSystemStatusOptions = [
  {
    label: 'Expected to close',
    value: 'expected-to-close',
  }, // 1 entry as of 2026/5/4
  {
    label: 'Expected to close 1st party',
    value: 'expected-to-close-1st-party',
  }, // 0 entries as of 2026/5/4
  {
    label: 'Expected to close 3rd party',
    value: 'expected-to-close-3rd-party',
  }, // 0 entries as of 2026/5/4
  {
    label: 'Closed by payer file',
    value: 'closed',
  }, // 0 entries as of 2026/5/4
  {
    label: 'Closed Unspecified',
    value: 'closed-unspecified',
  }, // 0 entries as of 2026/5/4
  {
    label: 'Closed by payer file',
    value: 'closed-by-payer-file',
  }, // 0 entries as of 2026/5/4
  {
    label: 'Closed by claims file',
    value: 'closed-by-claims-file',
  }, // 0 entries as of 2026/5/4
  {
    label: 'Open',
    value: 'open',
  }, // 259 entries as of 2026/5/4
  // value: 'invalid-gap', 12 entries as of 2026/5/4
  // invalid-gap automatically normalizes to 'open-gap'
] as const;

/** @deprecated use {@link SharedPackageRiskGapConfidenceRating RiskGapConfidenceRating} in @zeus-health/types instead */
export type RiskGapConfidenceRating = SharedPackageRiskGapConfidenceRating;
/** @deprecated use {@link RISK_GAP_CONFIDENCE_RATINGS} in @zeus-health/types instead */
export const riskGapConfidenceRatings: Array<RiskGapConfidenceRating> = [
  ...RISK_GAP_CONFIDENCE_RATINGS,
] as const;

export type RiskGapSystemStatus = (typeof riskGapSystemStatusOptions)[number]['value'];
export type RiskGapLegacyStatus = (typeof legacyRiskGapSystemStatusOptions)[number]['value'];
/** @deprecated use {@link RiskGapStatus} instead */
export type RiskGapBackwardsCompatibleStatus =
  | RiskGapUserStatus
  | RiskGapSystemStatus
  | RiskGapLegacyStatus;

const riskGapStatusOptions = [
  ...riskGapUserStatusOptions,
  ...riskGapSystemStatusOptions,
  ...legacyRiskGapSystemStatusOptions,
] as const;
export const riskGapStatuses: NonEmptyArray<RiskGapBackwardsCompatibleStatus> = [
  ...riskGapUserStatusOptions.map((option) => option.value),
  ...riskGapSystemStatusOptions.map((option) => option.value),
  ...legacyRiskGapSystemStatusOptions.map((option) => option.value),
] as NonEmptyArray<RiskGapBackwardsCompatibleStatus>;

export const riskGapDefaultSubstatuses = {
  open: [],
  pending: [
    {
      label: 'Sent to provider for review',
      value: 'pending-sent-to-provider-for-review',
    },
    {
      label: 'Condition being assessed',
      value: 'pending-condition-being-assessed',
    },
    {
      label: 'Other',
      value: 'pending-other',
    },
  ],
  'accepted-for-review': [
    {
      label: 'Sent to provider for review',
      value: 'accepted-for-review-sent-to-provider-for-review',
    },
    {
      label: 'Accepted by provider',
      value: 'accepted-for-review-accepted-by-provider',
    },
    {
      label: 'Condition being assessed',
      value: 'accepted-for-review-condition-being-assessed',
    },
    {
      label: 'Other',
      value: 'accepted-for-review-other',
    },
  ],
  dismissed: [
    {
      label: 'Acute in nature',
      value: 'acute-in-nature',
    },
    {
      label: 'Inaccurate or insufficient evidence',
      value: 'inaccurate-or-insufficient-evidence',
    },
    {
      label: 'Resolved',
      value: 'resolved',
    },
  ],
  'open-gap': [],
  closed: [],
  'closed-gap': [],
  'expected-to-close': [],
  'expected-to-close-1st-party': [],
  'expected-to-close-3rd-party': [],
  'closed-by-payer-file': [],
  'closed-by-claims-file': [],
  'closed-unspecified': [],
} as const satisfies Record<
  RiskGapBackwardsCompatibleStatus,
  Array<{ label: string; value: string }>
>;

export interface RiskGapStatusUpdate {
  id: string;
  status: RiskGapBackwardsCompatibleStatus;
  substatuses: Array<string>;
  note: string;
  created: string;
  createdBy: string;
}

export interface Evidence {
  resourceId: string;
  resourceType: string;
  description: string;
  citations: Array<string>;
}

export interface RiskGapEvidenceReference {
  resourceId: string;
  resourceType: string;
  display?: string;
  summary?: string;
  source: 'evaluatedResource' | 'relatedResourceExtension';
}

export const riskGapThumbsUpRatingReasons = [
  {
    label: 'Highlighted new information',
    value: 'highlighted-new-information',
  },
  {
    label: 'More accurate diagnosis code',
    value: 'more-accurate-diagnosis-code',
  },
  {
    label: 'Other',
    value: 'other',
  },
] as const;

export const riskGapThumbsDownRatingReasons = [
  {
    label: "There's a more appropriate code",
    value: 'theres-a-more-appropriate-code',
  },
  {
    label: 'Additional codes are needed',
    value: 'additional-codes-are-needed',
  },
  {
    label: 'More evidence is needed to confirm this code',
    value: 'more-evidence-is-needed-to-confirm-this-code',
  },
  {
    label: 'Other',
    value: 'other',
  },
] as const;

export class RiskGapModel extends FHIRModel<RiskGapMeasureReport> {
  public kind = 'MeasureReport' as const;
  private sharedModel: UnvalidatedRiskGapMeasureReportModel;

  public constructor(resource: RiskGapMeasureReport) {
    super(resource);
    this.sharedModel = new UnvalidatedRiskGapMeasureReportModel(resource);
  }

  public override get resource(): RiskGapMeasureReport {
    return this.sharedModel.getCoercedResource() as RiskGapMeasureReport;
  }

  public override set resource(value: RiskGapMeasureReport) {
    this.sharedModel = new UnvalidatedRiskGapMeasureReportModel(value);
  }

  public get isOpen(): boolean {
    return this.status === RISK_GAP_VALUES.MEASURE_REPORT.EXTENSION.RISK_GAP_STATUS_UPDATE.OPEN_GAP;
  }

  /**
   * Risk gaps are always editable regardless of status. They display this editable UI in one of two ways.
   *
   * Open gaps show the primary edit experience:
   * * Text edit component for status note at the base level of the detail view (without any dialog).
   * * Dedicated Pending/Dismiss/Accept buttons in the detail view.
   * * "Add to note" options within the evidence cards.
   *
   * Non-open gaps (pending/accepted/closed/etc) show the secondary edit experience:
   * * Notes and status are editable within an Edit Status dialog.
   * * No dedicated Pending/Dismiss/Accept buttons.
   * * No "Add to note" options within the evidence cards.
   */
  public get showPrimaryEditExperience(): boolean {
    return this.isOpen;
  }

  public get resourceType(): ResourceTypeString {
    return 'MeasureReport';
  }

  public get supersededByHccCodes(): Array<Coding> {
    const hierarchicalStatusExtension = this.resource?.group?.[0]?.extension?.find(
      (ext) => ext.url === RISK_EXTENSION_GAP_HIERARCHAL_STATUS,
    );
    const hierarchicalStatusCoding = hierarchicalStatusExtension?.valueCodeableConcept?.coding?.[0];
    const supersededByExtension = hierarchicalStatusCoding?.extension?.find(
      (ext) => ext.url === ZUS_SUPERSEDED_BY_SYSTEM,
    );
    const codes = supersededByExtension?.valueCodeableConcept?.coding;
    return codes?.filter((hcc) => hcc.system === CODE_SYSTEMS.HCC) ?? [];
  }

  public get newSupersededByHccCodes(): Array<Coding> {
    const supersededByExtension = this.resource?.extension?.find(
      (ext) => ext.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.SUPERSEDED_BY,
    );
    const codes = supersededByExtension?.valueCodeableConcept?.coding;
    return codes?.filter((hcc) => hcc.system === CODE_SYSTEMS.HCC) ?? [];
  }

  public get newSupersedingHccCodes(): Array<Coding> {
    const supersedingExtension = this.resource?.extension?.find(
      (ext) => ext.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.SUPERSEDING,
    );
    const codes = supersedingExtension?.valueCodeableConcept?.coding;
    return codes?.filter((hcc) => hcc.system === CODE_SYSTEMS.HCC) ?? [];
  }

  public get hierarchicalStatus(): string {
    const hierarchicalStatus = this.resource?.group?.[0]?.extension?.find(
      (ext) => ext.url === RISK_EXTENSION_GAP_HIERARCHAL_STATUS,
    )?.valueCodeableConcept?.coding?.[0]?.code;
    return hierarchicalStatus ?? 'applied-not-superseded';
  }

  public get evidence(): Array<Evidence> {
    if (!this.resource?.evaluatedResource?.[0]) {
      return [];
    }

    return this.resource.evaluatedResource.map((evidence) => {
      const [resourceType, resourceId] = (evidence.reference ?? '').split('/');
      return {
        resourceId,
        resourceType,
        description: evidence.display || this.title,
        citations: [],
      };
    });
  }

  public get evidenceReferences(): Array<RiskGapEvidenceReference> {
    const referencesByKey = new Map<string, RiskGapEvidenceReference>();
    const evidenceSummaryUrl = SYSTEM_URLS.EXTENSIONS.RISK_GAP.EVIDENCE_SUMMARY;

    const extractSummary = (extensions?: Array<ZusExtensionInterface>) =>
      extensions?.find((e) => e.url === evidenceSummaryUrl)?.valueString;

    const evaluatedResources = this.resource?.evaluatedResource ?? [];
    for (const evaluatedResource of evaluatedResources) {
      const [resourceType, resourceId] = (evaluatedResource.reference ?? '').split('/');
      if (!(resourceType && resourceId)) {
        continue;
      }

      referencesByKey.set(`${resourceType}|${resourceId}`, {
        resourceId,
        resourceType,
        display: evaluatedResource.display,
        summary: extractSummary(evaluatedResource.extension),
        source: 'evaluatedResource',
      });
    }

    const relatedRecordsExtension = this.resource.extension?.find(
      (extension) =>
        extension.url === RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECTS ||
        extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.RELATED_CLINICAL_OBJECTS,
    );

    const relatedRecordExtensions = relatedRecordsExtension?.extension ?? [];
    for (const relatedRecordExtension of relatedRecordExtensions) {
      if (
        relatedRecordExtension.url !== RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECT &&
        relatedRecordExtension.url !== SYSTEM_URLS.EXTENSIONS.RISK_GAP.RELATED_CLINICAL_OBJECT
      ) {
        continue;
      }

      const [resourceType, resourceId] = relatedRecordExtension.valueReference?.reference?.split(
        '/',
      ) ?? ['', ''];
      if (!(resourceType && resourceId)) {
        continue;
      }

      const existingReference = referencesByKey.get(`${resourceType}|${resourceId}`);
      if (existingReference) {
        if (!existingReference.display && relatedRecordExtension.valueReference?.display) {
          referencesByKey.set(`${resourceType}|${resourceId}`, {
            ...existingReference,
            display: relatedRecordExtension.valueReference.display,
          });
        }
        continue;
      }

      referencesByKey.set(`${resourceType}|${resourceId}`, {
        resourceId,
        resourceType,
        display: relatedRecordExtension.valueReference?.display,
        summary: extractSummary(relatedRecordExtension.valueReference?.extension),
        source: 'relatedResourceExtension',
      });
    }

    return Array.from(referencesByKey.values());
  }

  /** Reads the gap-level summary content from the MeasureReport summary extension. */
  public get gapSummaryContent(): string | undefined {
    const summaryExt = this.resource.extension?.find(
      (ext) => ext.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.SUMMARY,
    );
    return summaryExt?.extension?.find(
      (ext) => ext.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.SUMMARY_CONTENT,
    )?.valueString;
  }

  public get relatedRecords(): Array<FetchableResource> {
    const relatedRecordsExtension = this.resource.extension?.find(
      (extension) =>
        extension.url === RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECTS ||
        extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.RELATED_CLINICAL_OBJECTS,
    );

    return (
      compact(
        relatedRecordsExtension?.extension?.map((relatedRecordExtension) => {
          if (
            relatedRecordExtension.url !== RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECT &&
            relatedRecordExtension.url !== SYSTEM_URLS.EXTENSIONS.RISK_GAP.RELATED_CLINICAL_OBJECT
          ) {
            return null;
          }

          const [resourceType, resourceId] =
            relatedRecordExtension.valueReference?.reference?.split('/') ?? ['', ''];
          if (!(isFetchableResourceType(resourceType) && isValidResourceId(resourceId))) {
            return null;
          }

          return {
            resourceType,
            resourceId,
          };
        }),
      ) ?? []
    );
  }

  public get statusUpdates(): Array<RiskGapStatusUpdate> {
    const statusUpdatesExtension = this.resource.extension?.find(
      (extension) =>
        extension.url === RISK_EXTENSION_GAP_STATUS_UPDATES ||
        extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.STATUS_UPDATES,
    );

    const updates =
      statusUpdatesExtension?.extension
        ?.map((statusUpdateExtension, index): RiskGapStatusUpdate | null => {
          const legacyUpdatesStatus =
            statusUpdateExtension.extension?.find(
              (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_STATUS,
            )?.valueCodeableConcept?.coding?.[0]?.code ?? '';

          const newUpdatesStatus =
            statusUpdateExtension.extension?.find(
              (extension) => extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.STATUS_UPDATE_STATUS,
            )?.valueString ?? '';

          const statusValue = legacyUpdatesStatus || newUpdatesStatus;
          if (!statusValue) {
            return null;
          }

          if (legacyUpdatesStatus) {
            return {
              id: `${this.id}|${index}`,
              status: normalizeToBackwardsCompatibleStatus(legacyUpdatesStatus),
              substatuses:
                statusUpdateExtension.extension
                  ?.find(
                    (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_SUBSTATUSES,
                  )
                  ?.valueCodeableConcept?.coding?.map((coding) => coding.code)
                  ?.filter((c): c is string => !!c) ?? [],
              note:
                statusUpdateExtension.extension?.find(
                  (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_NOTE,
                )?.valueString ?? '',
              created: (() => {
                const createdExtension = statusUpdateExtension.extension?.find(
                  (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_CREATED,
                );
                return createdExtension?.valueDateTime ?? createdExtension?.valueDate ?? '';
              })(),
              createdBy:
                statusUpdateExtension.extension?.find(
                  (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_CREATED_BY,
                )?.valueString ?? '',
            };
          }

          if (newUpdatesStatus) {
            return {
              id: `${this.id}|${index}`,
              status: normalizeToBackwardsCompatibleStatus(newUpdatesStatus),
              substatuses:
                statusUpdateExtension.extension
                  ?.find(
                    (extension) =>
                      extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.STATUS_UPDATE_SUBSTATUSES,
                  )
                  ?.valueCodeableConcept?.coding?.map((coding) => coding.code)
                  ?.filter((c): c is string => !!c) ?? [],
              note:
                statusUpdateExtension.extension?.find(
                  (extension) =>
                    extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.STATUS_UPDATE_NOTE,
                )?.valueString ?? '',
              created: (() => {
                const createdExtension = statusUpdateExtension.extension?.find(
                  (extension) =>
                    extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.STATUS_UPDATE_CREATED,
                );
                return createdExtension?.valueDateTime ?? createdExtension?.valueDate ?? '';
              })(),
              createdBy:
                statusUpdateExtension.extension?.find(
                  (extension) =>
                    extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.STATUS_UPDATE_CREATED_BY,
                )?.valueString ?? '',
            };
          }

          return null;
        })
        .filter((u): u is RiskGapStatusUpdate => u !== null) ?? [];

    return updates.sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
  }

  public get lastStatusUpdate(): RiskGapStatusUpdate {
    const lastUpdate = this.statusUpdates.at(-1);

    // Exclude status updates with `status` values we don't recognize because we don't know how to display those.
    if (!(lastUpdate && isRiskGapStatus(lastUpdate.status))) {
      return {
        id: `${this.id}|`,
        status: 'open-gap',
        substatuses: [],
        note: '',
        created: this.resource.date ?? this.createdAt ?? formatISO(new Date()),
        createdBy: isPayerSource(this.gapSource)
          ? `${getPayerDisplay(this.gapSource)} Gap File`
          : 'Zus Automation',
      };
    }

    return lastUpdate;
  }

  public get lastStatusUpdateDate(): Date | undefined {
    const created = this.lastStatusUpdate.created;
    if (!created) {
      return undefined;
    }
    const date = new Date(created);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  public get lastUpdatedByDisplay(): string {
    // A URL in this field indicates a status update made by a Zus system
    if (
      this.lastStatusUpdate.createdBy.startsWith('http') ||
      this.lastStatusUpdate.createdBy.startsWith('zus-')
    ) {
      return 'Zus';
    }

    return this.lastStatusUpdate.createdBy;
  }

  public get icd10Codes(): Array<RiskGapICD10Code> {
    const codes = this.resource?.group?.[0]?.code?.coding;
    if (!codes) {
      return [];
    }
    return codes
      .filter(
        (icd10) => icd10.system === CODE_SYSTEMS.ICD_10 || icd10.system === CODE_SYSTEMS.ICD_10_CM,
      )
      .map((icd10) => {
        const extensions = (icd10 as { extension?: Array<ZusExtensionInterface> }).extension ?? [];
        const lastEncounterDiagnosisRecordedDate = extensions.find(
          (ext) =>
            ext.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.LATEST_ENCOUNTER_DIAGNOSIS_RECORDED_DATE,
        )?.valueDateTime;
        const sources = extensions
          .filter((ext) => ext.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.SOURCE)
          .map((ext) => ext.valueString)
          .filter((s): s is string => !!s);
        const dataSourceType = extensions
          .filter((ext) => ext.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.DATA_SOURCE_TYPE)
          .map((ext) => ext.valueString)
          .filter((s): s is string => !!s);
        return {
          code: icd10.code ?? '',
          display: icd10.display ?? '',
          lastEncounterDiagnosisRecordedDate,
          sources,
          dataSourceType,
        };
      });
  }

  public get hccCode(): string {
    return this.sharedModel.hccCode?.[0] ?? 'N/A';
  }

  public get title(): string {
    return this.resource.group?.[0]?.code?.coding?.[0]?.display ?? '';
  }

  public get type(): RiskGapType | NewGapType {
    return (this.sharedModel.type as RiskGapType | NewGapType) ?? 'unknown';
  }

  // Consider this function to be deprecated, given that a risk gap now has a list of gap sources
  // rather than a single source.  To maintain backwards compatibility (for now) we'll return
  // a source from the list using the some prioritization which was originally done in SQL.
  // See
  public get gapSource(): RiskGapSource | undefined {
    const gapSourceList = this.gapSourceList ?? this.gapSourceFromNewBackend;
    if (gapSourceList.includes('homeward-payer-data') || gapSourceList.includes('payer-gap')) {
      return 'homeward-payer-data';
    }
    if (gapSourceList.includes('zus-suspect-model') || gapSourceList.includes('zus-ai')) {
      return 'zus-suspect-model';
    }
    if (gapSourceList.includes('zus-recapture-model') || gapSourceList.includes('zus-rules')) {
      return 'zus-recapture-model';
    }

    return undefined;
  }

  public get gapSourceList(): Array<RiskGapSource | NewGapSource> {
    const reportExtensions = this.resource.extension ?? [];

    const legacySources = reportExtensions
      .filter((ext) => ext.url === GAP_SOURCE_URL)
      .map(
        (ext) =>
          ext.valueCodeableConcept?.coding?.find((c) => c.system === SYSTEM_ZUS_GAP_SOURCE)?.code,
      )
      .filter((code): code is RiskGapSource => !!code);

    const newBackendSources = this.gapSourceFromNewBackend.filter(
      (code): code is NewGapSource => !!code,
    );
    const displayList = Array.from(
      new Set<RiskGapSource | NewGapSource>([...legacySources, ...newBackendSources]),
    );

    // This will all be removed later, for now we exclude user-update as a possible source
    return displayList.filter((item) => item !== 'user-update');
  }

  public get gapSourceListDisplay(): string {
    return [...new Set(this.gapSourceList.map(this.gapSourceToString))]
      .sort()
      .filter(Boolean)
      .join(', ');
  }

  private readonly gapSourceToString = (
    source: RiskGapSource | NewGapSource | undefined,
  ): string => {
    switch (source) {
      case 'zus-rules':
      case 'zus-ai':
      case 'zus-suspect-model':
      case 'zus-recapture-model':
        return 'Zus';
      case 'payer-gap':
      case 'homeward-payer-data':
        return getPayerDisplay(source);
      default:
        return '';
    }
  };

  public get gapSourceFromNewBackend(): Array<string> {
    const statusUpdatesExtension = this.resource?.extension?.find(
      (extension) => extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.STATUS_UPDATES,
    );

    const statusUpdates = statusUpdatesExtension?.extension ?? [];
    if (statusUpdates.length === 0) {
      return [];
    }

    const sources = statusUpdates
      .flatMap((statusUpdate) => statusUpdate.extension ?? [])
      .filter((e) => e.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP.STATUS_UPDATE_SOURCE)
      .map((e) => e.valueString)
      .filter((v): v is string => typeof v === 'string' && v.trim().length > 0);

    return Array.from(new Set(sources));
  }

  public get rafScore(): number | null {
    return this.sharedModel.measureScore ?? null;
  }

  public get status(): RiskGapStatus {
    // Use shared model for new-format extensions (returns raw valueString).
    const sharedStatus = this.sharedModel.status;
    if (sharedStatus && isRiskGapStatus(sharedStatus)) {
      return sharedStatus as unknown as RiskGapStatus;
    }
    // Fall back to legacy status update extraction when the shared model finds nothing
    // (i.e., data uses legacy extension URLs that the shared model doesn't recognize).
    if (sharedStatus === undefined) {
      return (this.lastStatusUpdate.status as RiskGapStatus) ?? 'open-gap';
    }
    return 'open-gap';
  }

  public get statusSortRank(): number {
    const RISK_GAP_LIST_STATUS_SORT_RANK: Partial<Record<RiskGapStatus, number>> = {
      'open-gap': 0,
      pending: 1,
      'accepted-for-review': 2,
      dismissed: 3,
      'expected-to-close-1st-party': 4,
      'expected-to-close-3rd-party': 4,
      'closed-gap': 4,
    } as const;

    return RISK_GAP_LIST_STATUS_SORT_RANK[this.status] ?? 99;
  }

  /** For patient list sorting: recapture, suspect, net-new, unknown. */
  public get typeSortRank(): number {
    const RISK_GAP_LIST_TYPE_SORT_RANK: Record<RiskGapType | NewGapType, number> = {
      recapture: 0,
      suspect: 1,
      'net-new': 2,
      unknown: 3,
    } as const;

    return RISK_GAP_LIST_TYPE_SORT_RANK[this.type as RiskGapType | NewGapType] ?? 99;
  }

  public get statusDisplay(): string {
    if (this.status === 'pending') {
      return `Pending ${formatDateToLocalString(this.lastStatusUpdateDate, false)} by ${this.lastUpdatedByDisplay}`;
    }

    if (this.status === 'accepted-for-review') {
      return `Accepted for review ${formatDateToLocalString(this.lastStatusUpdateDate, false)} by ${this.lastUpdatedByDisplay}`;
    }

    if (this.status === 'dismissed') {
      return `Dismissed ${formatDateToLocalString(this.lastStatusUpdateDate, false)} by ${this.lastUpdatedByDisplay}`;
    }

    if (this.status.includes('expected-to-close')) {
      // To account for new 1st and 3rd party statuses
      return 'Expected to Close by Encounter Diagnosis';
    }

    if ((this.status as unknown) === 'closed-by-payer-file') {
      return `Closed ${formatDateToLocalString(this.lastStatusUpdateDate, false)} by Payer file`;
    }

    if (this.status === 'closed-gap') {
      return 'Closed';
    }

    if ((this.status as unknown) === 'closed-unspecified') {
      return 'Closed';
    }

    if ((this.status as unknown) === 'closed-by-claims-file') {
      return `Closed ${formatDateToLocalString(this.lastStatusUpdateDate, false)} by Claims file`;
    }

    return `Opened ${formatDateToLocalString(this.lastStatusUpdateDate, false)} by ${this.lastUpdatedByDisplay}`;
  }

  public get substatuses(): Array<string> {
    return this.lastStatusUpdate.substatuses ?? [];
  }

  public get suspectedDisplay(): string | undefined {
    // SuspectedDate only applies to suspect risk gaps, where it is simply the date field on the
    // MeasureReport.
    if (this.type !== 'suspect') {
      return undefined;
    }
    return this.resource.date ? formatFHIRDate(this.resource.date) : undefined;
  }

  public get lastDiagnosedDisplay(): string | undefined {
    const evidenceStatusDateExtension = this.resource?.group?.[0]?.extension?.find(
      (ext) => ext.url === RA_EVIDENCE_STATUS_DATE_URL,
    )?.valueDate;
    return evidenceStatusDateExtension ? formatFHIRDate(evidenceStatusDateExtension) : undefined;
  }

  public get confidenceLevel(): number | null {
    if (this.type === 'recapture' || this.type === 'net-new') {
      return 5;
    }
    return this.sharedModel.confidenceLevel ?? null;
  }

  public get confidenceRating(): RiskGapConfidenceRating | undefined {
    switch (this.confidenceLevel) {
      case 1:
      case 2:
      case 3:
        return 'low';
      case 4:
        return 'medium';
      case 5:
        return 'high';
      default:
        return undefined;
    }
  }

  public get period(): string | undefined {
    if (!this.resource.period?.start) {
      return undefined;
    }

    return getYearFromISODate(this.resource.period.start);
  }
}

// Constants for FHIR extension URLs
const RA_EVIDENCE_STATUS_DATE_URL =
  'http://hl7.org/fhir/us/davinci-ra/StructureDefinition/ra-evidenceStatusDate';
export const GAP_SOURCE_URL = 'https://zusapi.com/fhir/identifier/quality/riskgap/gap-source';
